import type { GetStaticProps, NextPage } from "next"
import { paramCase } from "change-case"

import { linkResolver } from "~/api/prismic/linkResolver"
import { getContributePage } from "~/api/prismic/singles"
import type { ContributePage } from "~/api/prismic/types"
import { ContributeButton } from "~/components/ContributeLink/Button"
import CustomRichText from "~/components/CustomRichText"
import { FormalLayout, TitledSection } from "~/components/FormalLayout"
import { SEO } from "~/components/SEO"
import { withLoadingFallback } from "~/utils/hocs/withLoadingFallback"
import { enhanceStaticProps } from "~/utils/next/enhanceStaticProps"
import { getPreviewRef } from "~/utils/prismic/previewData"
import * as RichText from "~/utils/prismic/richText"

type ContributePageProps = { pageContent: ContributePage }

const Contribute: NextPage<ContributePageProps> = ({ pageContent }) => {
  const { body, title } = pageContent.data

  const titleText = RichText.asText(title)

  const sectionTitles = body.map((item) => {
    switch (item.slice_type) {
      case "section": {
        const sectionTitle = RichText.asText(item.primary.section_title)
        const titleId = paramCase(sectionTitle)

        return { title: sectionTitle, id: titleId }
      }

      case "open_collective":
      default:
        return {}
    }
  })

  return (
    <>
      <SEO pageTitle={titleText} path={linkResolver(pageContent)} />
      <FormalLayout title={titleText} sectionTitles={sectionTitles}>
        {body.map((item, index) => {
          switch (item.slice_type) {
            case "open_collective": {
              return (
                <div key={index} className="flex items-center justify-center">
                  <ContributeButton url={item.primary.url} />
                </div>
              )
            }

            case "section": {
              const { title: sectionTitle, id } = sectionTitles[index]

              return (
                <TitledSection key={id} id={id} title={sectionTitle}>
                  <CustomRichText field={item.primary.section_body} />
                </TitledSection>
              )
            }

            default:
              return null
          }
        })}
      </FormalLayout>
    </>
  )
}

export const getStaticProps: GetStaticProps<ContributePageProps> =
  enhanceStaticProps<ContributePageProps>(async ({ previewData }) => {
    const res = await getContributePage({
      ref: getPreviewRef(previewData),
    })

    if (!res) {
      return { notFound: true }
    }

    return {
      props: { pageContent: res },
      revalidate: 15,
    }
  })

export default withLoadingFallback(Contribute)
